import { useEffect, useState } from 'react'
import { BBDTag, DataItem, DataItemRow, Texto, Horizontal, MomentDatePicker } from '@gravitate-js/excalibrr'
import { Col, Row, Tag, Form, Switch, Button, Input, Select } from 'antd'
import moment from 'moment'
import api from '@utils/api'
import { PayConfigModal } from './PayConfigModal'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { ReadOutlined, EditOutlined } from '@ant-design/icons'
import {
  getSupplyOptionsModeText,
  getTripModeText,
  IN_CAB_SUPPLY_OPTIONS_MODE,
  IN_CAB_TRIP_MODE_OPTIONS,
} from '@components/DriverManagement/Manage/Form/util'
import { DropdownSelectElement } from './DropdownSelectElement'

export const DriverHeader = ({ driver, payConfigNames, editing, creatingDriver, setEditing }) => {
  const { scopes } = useSecurityContext()
  const [showingModal, setShowingModal] = useState(false)
  const [editingConfig, setEditingConfig] = useState(false)
  const [payConfigs, setPayConfigs] = useState([])

  const driverInfoClass = editing ? 'pt-3 editing-driver-info' : 'pb-4'
  const canEditPayConfig = scopes.driver?.automatic_pay

  useEffect(() => {
    let isMounted = true
    if (canEditPayConfig && driver)
      api.getDriverPayConfig(driver.id).then(({ data }) => {
        isMounted && setPayConfigs(data)
        isMounted && setEditingConfig(false)
      })
    return () => (isMounted = false)
  }, [driver])

  const addDriverPayConfig = () => {
    const copy = [...payConfigs]
    copy.push({ name: null, start_date: null, end_date: null })
    setPayConfigs(copy)
    setEditingConfig(true)
  }

  const saveDriverPayConfig = (config) => {
    const copy = [...payConfigs]
    const newConfig = copy[copy.length - 1]
    newConfig.name = config.name
    newConfig.start_date = config.date

    if (copy.length > 1) {
      const previousConfig = copy[copy.length - 2]
      previousConfig.end_date = newConfig.start_date
    }
    api.saveDriverPayConfig(driver.id, config.name, moment(config.date).format('YYYY-MM-DDTHH:mm:ss'))
    setPayConfigs(copy)
    setEditingConfig(false)
  }

  const cancelChanges = () => {
    let copy = [...payConfigs]
    copy = copy.slice(0, -1)
    setPayConfigs(copy)
    setEditingConfig(false)
  }
  if (!creatingDriver && (!driver || !payConfigNames)) return null
  return (
    <div className={driverInfoClass}>
      {payConfigNames && (
        <PayConfigModal
          driver={driver}
          payConfigs={payConfigs}
          editingConfig={editingConfig}
          payConfigNames={payConfigNames}
          showingModal={showingModal}
          setShowingModal={setShowingModal}
          addDriverPayConfig={addDriverPayConfig}
          saveDriverPayConfig={saveDriverPayConfig}
          cancelChanges={cancelChanges}
        />
      )}
      <Row style={{ flex: 3 }}>
        {editing || creatingDriver ? (
          <Col span={12}>
            <EditView driver={driver} editing={editing} creatingDriver={creatingDriver} />
          </Col>
        ) : (
          <ReadView driver={driver} />
        )}
        <ActionStatusControls
          status={driver ? driver.status : 'NEW DRIVER'}
          editing={editing}
          creatingDriver={creatingDriver}
          setEditing={setEditing}
          payConfigs={payConfigs}
          setShowingModal={setShowingModal}
          canEditPayConfig={canEditPayConfig}
        />
      </Row>
    </div>
  )
}

const ReadView = ({ driver }) => {
  if (driver)
    return (
      <>
        <Col className='pt-2 pl-0' span={12} align='left'>
          <div className='driver-name-header'>{driver.name}</div>
          <Horizontal className='driver-position mt-2' verticalCenter>
            <Tag color='rgba(118, 171, 243, 0.3)' className='driver-title-tag'>
              {driver.title}
            </Tag>
            <BBDTag className='driver-status-tag' success={driver.active === true} error={driver.active === false}>
              {driver.active ? 'Active' : 'Inactive'}
            </BBDTag>
            <BBDTag>
              <Horizontal verticalCenter>
                <ReadOutlined className={'mr-2'} />
                {getTripModeText(driver.in_cab_trip_mode)}
              </Horizontal>
            </BBDTag>
          </Horizontal>
          <div className='driver-joined mt-2'>
            {getJoinYearLabel(driver.hire_date) + ' ' + formatters.DATE(moment(driver.hire_date))}
          </div>
          <div className='flex-div'>
            <DataItem label='Username:'>
              <b>{driver.username}</b>
            </DataItem>
          </div>

          <Horizontal className='manager-label mt-1'>
            <DataItem className='driver-manager' label='Supervisor:'>
              <Texto appearance='secondary' weight='bold'>
                {driver.supervisor}
              </Texto>
            </DataItem>
          </Horizontal>
          <Horizontal className='manager-label mt-1'>
            <DataItem className='driver-manager' label='In-Cab Supply Options Mode:'>
              <Texto>{getSupplyOptionsModeText(driver.in_cab_supply_option_mode)}</Texto>
            </DataItem>
          </Horizontal>
        </Col>
      </>
    )
}

const EditView = ({ driver, editing, creatingDriver }) => {
  let name = ''
  let title = ''
  let supervisor = ''
  let username = ''

  if (driver) {
    name = driver.name
    title = driver.title
    supervisor = driver.supervisor
    username = driver.username
  }

  return (
    <Col span={16}>
      <Texto category='h5' className='pt-2'>
        General
      </Texto>
      <DriverStatusToggle driver={driver} editing={editing} />
      <DataItemRow label='Full Name' labelSpan={8} valueSpan={16}>
        {DriverInputControl(name, 'name', editing, 'First & Last Name')}
      </DataItemRow>
      <DataItemRow label='Username' labelSpan={8} valueSpan={16}>
        {DriverInputControl(username, 'username', editing, 'Username')}
      </DataItemRow>
      {!driver && editing && (
        <DataItemRow label='Password' labelSpan={8} valueSpan={16}>
          <Form.Item name='password' rules={[{ required: true, message: 'Password is required for new drivers.' }]}>
            <Input placeholder='Enter Password' />
          </Form.Item>
        </DataItemRow>
      )}
      <DataItemRow label='Title' labelSpan={8} valueSpan={16}>
        {DriverInputControl(title, 'title', editing, 'Title', false)}
      </DataItemRow>
      <HireDateControl driver={driver} />
      {!creatingDriver && <TermDateControl driver={driver} />}
      <DataItemRow label='Supervisor' labelSpan={8} valueSpan={16}>
        {DriverInputControl(supervisor, 'supervisor', editing, 'Supervisor')}
      </DataItemRow>
      <DataItemRow label='In-Cab Trip Mode' labelSpan={8} valueSpan={16}>
        <DriverSelectControl
          options={IN_CAB_TRIP_MODE_OPTIONS}
          name='in_cab_trip_mode'
          placeholder='Select In-Cab Trip Mode'
        />
      </DataItemRow>
      <DataItemRow label='In-Cab Supply Options Mode' labelSpan={8} valueSpan={16}>
        <DriverSelectControl
          options={IN_CAB_SUPPLY_OPTIONS_MODE}
          name='in_cab_supply_option_mode'
          placeholder='Select In-Cab Supply Options Mode'
        />
      </DataItemRow>
    </Col>
  )
}

const getJoinYearLabel = (date) => {
  const currentYear = moment().format('YYYY')
  const joinYear = moment(date).format('YYYY')

  if (joinYear < currentYear) return `Joined Company ${currentYear - joinYear} years ago.`
  else return 'Joined Company this year.'
}

const DriverInputControl = (value, name, editing, placeholder, required = true) => {
  const message = `${placeholder} is required`
  return (
    <div className='driver-form-line'>
      <Form.Item name={name} rules={[{ required: required, message: message }]}>
        {editing ? <Input placeholder={placeholder} /> : <div>{value}</div>}
      </Form.Item>
    </div>
  )
}

const DriverStatusToggle = ({ driver, editing }) => {
  if (!driver) return null
  if (editing)
    return (
      <Col span={8}>
        <div className='flex justify-start'>
          <DataItem label='Active' className='driver-form-line'>
            <Form.Item name='active'>
              <Switch
                width='100%'
                defaultChecked={driver.active}
                checkedChildren='Active'
                unCheckedChildren='Inactive'
              />
            </Form.Item>
          </DataItem>
        </div>
      </Col>
    )
}

function DriverSelectControl({ options, name, placeholder }) {
  return (
    <Form.Item name={name} className='driver-form-line' initialValue={options[0].value}>
      <Select
        placeholder={placeholder}
        options={options.map(({ value, title, description }) => ({
          value,
          title,
          label: <DropdownSelectElement title={title} description={description} />,
        }))}
        optionLabelProp='title'
      />
    </Form.Item>
  )
}

const HireDateControl = ({ driver }) => (
  <div className='driver-form-line'>
    <DataItemRow label='Hire Date' labelSpan={8} valueSpan={16}>
      <Form.Item name='hire_date' rules={[{ required: true, message: 'Hire Date is required' }]}>
        <MomentDatePicker format={formatters.DATE} />
      </Form.Item>
    </DataItemRow>
  </div>
)

const TermDateControl = ({ driver }) => (
  <div className='driver-form-line'>
    <DataItemRow label='Termination Date' labelSpan={8} valueSpan={16}>
      <Form.Item name='termination_date'>
        <MomentDatePicker format={formatters.DATE} />
      </Form.Item>
    </DataItemRow>
  </div>
)

const ActionStatusControls = ({ editing, creatingDriver, payConfigs, setShowingModal, canEditPayConfig }) => {
  const editPadding = editing ? 'pl-0' : 'pt-3 '
  return (
    <Col span={12} align='right'>
      {!creatingDriver && (
        <>
          <Row className={editPadding}>
            <Col span={24} align='right'>
              <Col span={12} align='left'>
                <Row>
                  <Col className='driver-pay-label' span={12}>
                    <Texto category='h5'>{`DRIVER PAY`}</Texto>
                  </Col>
                  {canEditPayConfig && (
                    <Col className='edit-driver-pay pb-1' span={12} align='right'>
                      <Button style={{ marginLeft: '24px' }} appearance='outline' onClick={() => setShowingModal(true)}>
                        {<EditOutlined />}
                        Edit Pay Config
                      </Button>
                    </Col>
                  )}
                </Row>
                <Row className='pt-3' style={{ display: 'flex', alignItems: 'space-between', width: 300 }}>
                  <Col className='payroll-config-label' span={12}>
                    <Texto category='label'>Payroll Configuration</Texto>
                  </Col>
                  <Col className='payroll-config-display' span={12} align='right'>
                    <Texto category='label'>
                      {payConfigs && payConfigs.length > 0 && payConfigs[payConfigs.length - 1].name
                        ? payConfigs[payConfigs.length - 1].name
                        : 'N/A'}
                    </Texto>
                  </Col>
                </Row>
              </Col>
            </Col>
          </Row>
        </>
      )}
    </Col>
  )
}
